import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertTriangle, CheckCircle, AlertCircle, Eye } from 'lucide-react';

const categoryStyles = {
  natural: 'ingredient-natural',
  additive: 'ingredient-additive',
  preservative: 'ingredient-preservative',
  sweetener: 'ingredient-sweetener',
  coloring: 'ingredient-coloring',
  flavor: 'ingredient-flavor',
  emulsifier: 'ingredient-emulsifier',
  other: 'ingredient-other',
};

const healthImpactIcons = {
  safe: { icon: CheckCircle, color: 'text-emerald-500' },
  caution: { icon: AlertCircle, color: 'text-amber-500' },
  avoid: { icon: AlertTriangle, color: 'text-red-500' },
};

export const IngredientTag = ({ ingredient, showDetails = false }) => {
  const { name, category, health_impact, description, is_hidden } = ingredient;
  
  const categoryClass = categoryStyles[category?.toLowerCase()] || categoryStyles.other;
  const healthConfig = healthImpactIcons[health_impact?.toLowerCase()] || healthImpactIcons.caution;
  const HealthIcon = healthConfig.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`ingredient-tag ${categoryClass} cursor-help gap-1.5 py-1.5 px-3`}
            data-testid={`ingredient-tag-${name?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <HealthIcon className={`w-3 h-3 ${healthConfig.color}`} />
            <span className="font-medium">{name}</span>
            {is_hidden && (
              <Eye className="w-3 h-3 text-purple-500" />
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{name}</span>
              {is_hidden && (
                <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                  Hidden
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="outline" className={`text-xs ${categoryClass}`}>
                {category}
              </Badge>
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  health_impact === 'safe' ? 'bg-emerald-50 text-emerald-700' :
                  health_impact === 'caution' ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700'
                }`}
              >
                {health_impact}
              </Badge>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default IngredientTag;
